import { useEffect, useState } from "react";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import z from "zod";

import type {
  Organisation,
  OrganisationConfigurationHierarchyUnbranded,
  OrganisationConfigurationJsonUnbranded,
} from "@thalamos/vision-common";
import {
  AvailableLanguagesSchema,
  mergeConfigurations,
  OrganisationArraySchema,
  OrganisationConfigurationJsonSchema,
  WorkflowVersionValues,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../../../api/index.js";
import { useToastNotifications } from "../../../../hooks/useToastNotifications.js";
import { createRoute } from "../../../../routing/createRoute.js";

// Exclude embedDashboard from generic field types as it has a dedicated component
type SimpleConfigFields = Exclude<
  keyof OrganisationConfigurationJsonUnbranded,
  "embedDashboard"
>;

function OverrideableMultiStringConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  field: SimpleConfigFields;
  label: string;
  inheritedConfig: Partial<OrganisationConfigurationJsonUnbranded>;
  configuration: Partial<OrganisationConfigurationJsonUnbranded>;
  setConfiguration: (
    config: Partial<OrganisationConfigurationJsonUnbranded>,
  ) => void;
}) {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={configuration[field] !== undefined}
              onChange={() => {
                if (configuration[field] === undefined) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field],
                  });
                } else {
                  // Remove the key entirely, rather than just setting to undefined
                  const temp = { ...configuration };
                  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                  delete temp[field];
                  setConfiguration({
                    ...temp,
                  });
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        {configuration[field] === undefined ? (
          <Box>{(inheritedConfig[field] as string[]).join(", ")}</Box>
        ) : (
          <>
            {(configuration[field] as string[]).map(
              (item: string, index: number) => (
                <Box
                  key={`box-${String(index)}`}
                  sx={{ display: "flex", gap: "1rem" }}
                >
                  <TextField
                    label={label}
                    value={item}
                    onChange={(event) => {
                      const newItems = (configuration[field] as string[]).map(
                        (val, i) => (i === index ? event.target.value : val),
                      );
                      setConfiguration({
                        ...configuration,
                        [field]: newItems,
                      });
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      const newItems = (
                        configuration[field] as string[]
                      ).filter((i) => i !== item);
                      setConfiguration({
                        ...configuration,
                        [field]: newItems,
                      });
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ),
            )}
            <IconButton
              onClick={() => {
                const newItems = [...(configuration[field] as string[]), ""];
                setConfiguration({
                  ...configuration,
                  [field]: newItems,
                });
              }}
            >
              <Add />
            </IconButton>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableNumberConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  field: SimpleConfigFields;
  label: string;
  inheritedConfig: Partial<OrganisationConfigurationJsonUnbranded>;
  configuration: Partial<OrganisationConfigurationJsonUnbranded>;
  setConfiguration: (
    config: Partial<OrganisationConfigurationJsonUnbranded>,
  ) => void;
}) {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={configuration[field] !== undefined}
              onChange={() => {
                if (configuration[field] === undefined) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field] ?? 0,
                  });
                } else {
                  // Remove the key entirely, rather than just setting to undefined
                  const temp = { ...configuration };
                  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                  delete temp[field];
                  setConfiguration({
                    ...temp,
                  });
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        {configuration[field] === undefined ? (
          <Box>{String(inheritedConfig[field])}</Box>
        ) : (
          <>
            {
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <TextField
                  label={label}
                  value={configuration[field]}
                  type="number"
                  onChange={(event) => {
                    const value = event.target.value;
                    setConfiguration({
                      ...configuration,
                      [field]: value === "" ? 0 : Number(value),
                    });
                  }}
                />
              </Box>
            }
          </>
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableSelectValueConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
  options,
}: {
  field: SimpleConfigFields;
  label: string;
  inheritedConfig: Partial<OrganisationConfigurationJsonUnbranded>;
  configuration: Partial<OrganisationConfigurationJsonUnbranded>;
  setConfiguration: (
    config: Partial<OrganisationConfigurationJsonUnbranded>,
  ) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={configuration[field] !== undefined}
              onChange={() => {
                if (configuration[field] === undefined) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field],
                  });
                } else {
                  // Remove the key entirely, rather than just setting to undefined
                  const temp = { ...configuration };
                  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                  delete temp[field];
                  setConfiguration({
                    ...temp,
                  });
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        {configuration[field] === undefined ? (
          <Box>{String(inheritedConfig[field])}</Box>
        ) : (
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">{label}</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={configuration[field] ?? ""}
              label="Age"
              onChange={(event) => {
                setConfiguration({
                  ...configuration,
                  [field]: event.target.value,
                });
              }}
            >
              {options.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableBooleanConfiguration({
  field,
  label,
  inheritedConfig,
  configuration,
  setConfiguration,
}: {
  field: SimpleConfigFields;
  label: string;
  inheritedConfig: Partial<OrganisationConfigurationJsonUnbranded>;
  configuration: Partial<OrganisationConfigurationJsonUnbranded>;
  setConfiguration: (
    config: Partial<OrganisationConfigurationJsonUnbranded>,
  ) => void;
}) {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={configuration[field] !== undefined}
              onChange={() => {
                if (configuration[field] === undefined) {
                  setConfiguration({
                    ...configuration,
                    [field]: inheritedConfig[field] ?? false,
                  });
                } else {
                  // Remove the key entirely, rather than just setting to undefined
                  const temp = { ...configuration };
                  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                  delete temp[field];
                  setConfiguration({
                    ...temp,
                  });
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Checkbox
              disabled={configuration[field] === undefined}
              checked={
                configuration[field] === undefined
                  ? (inheritedConfig[field] as boolean)
                  : (configuration[field] as boolean)
              }
              onChange={() => {
                setConfiguration({
                  ...configuration,
                  [field]: !configuration[field],
                });
              }}
            />
          }
          label={"Enable Feature"}
        />
      </TableCell>
    </TableRow>
  );
}

function OverrideableCommaSeparatedArrayConfiguration({
  field,
  label,
  helperText,
  inheritedConfig,
  configuration,
  setConfiguration,
  validate,
}: {
  field: SimpleConfigFields;
  label: string;
  helperText?: string;
  inheritedConfig: Partial<OrganisationConfigurationJsonUnbranded>;
  configuration: Partial<OrganisationConfigurationJsonUnbranded>;
  setConfiguration: (
    config: Partial<OrganisationConfigurationJsonUnbranded>,
  ) => void;
  validate?: (item: string) => boolean;
}) {
  // Store the text input value for comma-separated editing
  const [textValue, setTextValue] = useState(
    (configuration[field] as string[] | undefined)?.join(", ") ?? "",
  );

  // Update text value when configuration changes externally
  useEffect(() => {
    if (configuration[field] !== undefined) {
      setTextValue((configuration[field] as string[]).join(", "));
    }
  }, [configuration, field]);

  const parseAndSave = (value: string) => {
    const items = value
      .split(",")
      .map((p) => p.toUpperCase().trim())
      .filter((p) => p.length > 0 && (validate ? validate(p) : true));
    // Remove duplicates
    const uniqueItems = [...new Set(items)];
    setConfiguration({
      ...configuration,
      [field]: uniqueItems,
    });
  };

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={configuration[field] !== undefined}
              onChange={() => {
                if (configuration[field] === undefined) {
                  const inherited = inheritedConfig[field] as
                    string[] | undefined;
                  setConfiguration({
                    ...configuration,
                    [field]: inherited ?? [],
                  });
                  setTextValue(inherited?.join(", ") ?? "");
                } else {
                  const temp = { ...configuration };
                  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                  delete temp[field];
                  setConfiguration({
                    ...temp,
                  });
                  setTextValue("");
                }
              }}
            />
          }
          label={`Override`}
        />
      </TableCell>
      <TableCell>
        {configuration[field] === undefined ? (
          <Box>
            {((inheritedConfig[field] as string[] | undefined) ?? []).join(
              ", ",
            ) || "(none)"}
          </Box>
        ) : (
          <TextField
            label={label}
            fullWidth
            multiline
            rows={2}
            value={textValue}
            helperText={helperText}
            onChange={(event) => {
              setTextValue(event.target.value);
            }}
            onBlur={() => {
              parseAndSave(textValue);
            }}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function OverrideableEmbedDashboardConfiguration({
  configuration,
  setConfiguration,
}: {
  configuration: Partial<OrganisationConfigurationJsonUnbranded>;
  setConfiguration: (
    config: Partial<OrganisationConfigurationJsonUnbranded>,
  ) => void;
}) {
  const embedDashboard = configuration.embedDashboard;

  return (
    <TableRow>
      <TableCell>Embed Dashboard</TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={embedDashboard !== undefined}
              onChange={() => {
                if (embedDashboard === undefined) {
                  setConfiguration({
                    ...configuration,
                    embedDashboard: { dashboardId: "", embeddedUuid: "" },
                  });
                } else {
                  const { embedDashboard: _, ...rest } = configuration;
                  setConfiguration(rest);
                }
              }}
            />
          }
          label="Override"
        />
      </TableCell>
      <TableCell>
        {embedDashboard === undefined ? (
          <Box>unconfigured</Box>
        ) : (
          <Box sx={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
            <TextField
              label="Dashboard ID"
              value={embedDashboard.dashboardId}
              onChange={(event) => {
                setConfiguration({
                  ...configuration,
                  embedDashboard: {
                    ...embedDashboard,
                    dashboardId: event.target.value,
                  },
                });
              }}
            />
            <TextField
              label="Embedded UUID"
              value={embedDashboard.embeddedUuid}
              onChange={(event) => {
                setConfiguration({
                  ...configuration,
                  embedDashboard: {
                    ...embedDashboard,
                    embeddedUuid: event.target.value,
                  },
                });
              }}
            />
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}

async function saveConfig(
  organisation: Organisation,
  configuration: OrganisationConfigurationJsonUnbranded,
) {
  const safeParse =
    OrganisationConfigurationJsonSchema.safeParse(configuration);

  if (!safeParse.success) {
    throw new Error(
      `Invalid configuration ${z.prettifyError(safeParse.error)}`,
    );
  }

  if (
    configuration.isTrainingOrganisation &&
    !organisation.name.toLowerCase().includes("test") &&
    !organisation.name.toLowerCase().includes("training")
  ) {
    throw new Error("Name must include 'test' or 'training' for training orgs");
  }

  const response = await api.organisation.setOrgConfig(
    organisation.id,
    configuration,
  );

  if (response.status !== 204) {
    throw new Error("Failed to save configuration");
  }
  return response;
}

function OrgConfigUpdateInner({
  organisation,
  organisationData,
}: {
  organisation: Organisation;
  organisationData: OrganisationConfigurationHierarchyUnbranded;
}) {
  const inheritedOrganisationHierarchy = organisationData.filter(
    (o) => o.organisationId !== organisation.id,
  );

  const inheritedConfig = mergeConfigurations(
    organisation.managingOrganisationId,
    inheritedOrganisationHierarchy,
  );

  const orgConfig = organisationData.find(
    (o) => o.organisationId === organisation.id,
  )?.configuration;

  const [configuration, setConfiguration] =
    useState<Partial<OrganisationConfigurationJsonUnbranded | undefined>>(
      orgConfig,
    );
  const [submitting, setSubmitting] = useState(false);
  const notifications = useToastNotifications();
  const navigate = useNavigate();

  return (
    configuration && (
      <Box>
        <Typography variant="h6" component="div">
          Update Organisation Config
        </Typography>
        <Box>
          <OverrideableMultiStringConfiguration
            field={"associatedEmailDomains"}
            label="Associated Email Domains"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableSelectValueConfiguration
            field={"workflowVersion"}
            label="Workflow Version"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
            options={[
              ...Object.values(WorkflowVersionValues).map((lang) => ({
                value: lang,
                label: lang,
              })),
            ]}
          />
          <OverrideableSelectValueConfiguration
            field={"language"}
            label="Language"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
            options={[
              ...Object.values(AvailableLanguagesSchema.enum).map((lang) => ({
                value: lang,
                label: lang,
              })),
            ]}
          />
          <OverrideableNumberConfiguration
            field="amberS136ThresholdHours"
            label="S136 Amber Alert Threshold (hours)"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableNumberConfiguration
            field="redS136ThresholdHours"
            label="S136 Red Alert Threshold (hours)"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableBooleanConfiguration
            field="isTrainingOrganisation"
            label="Training Organisation (Name must include 'test' or 'training')"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableBooleanConfiguration
            field="patientReconciliationEnabled"
            label="Patient Reconciliation Enabled"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableBooleanConfiguration
            field="mentalCapacityActEnabled"
            label="Mental Capacity Act Enabled"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableBooleanConfiguration
            field="snapshotDownloadEnabled"
            label="Snapshot Download Enabled"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableBooleanConfiguration
            field="incidentSummaryViewEnabled"
            label="Incident Summary View Enabled"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableBooleanConfiguration
            field="officerLookupEnabled"
            label="Officer Lookup Enabled"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableCommaSeparatedArrayConfiguration
            field="forceAreaPostcodePrefixes"
            label="Force Area Postcode Prefixes"
            helperText="Comma-separated list of UK postcode prefixes (e.g., EC, WC, N, E, SE, SW, W, NW). If configured, shows warning when selecting POS outside these areas."
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
            validate={(item) => item.length >= 1 && item.length <= 2}
          />
          <OverrideableNumberConfiguration
            field="intervalBetweenUpdateIncidentEmailsMinutes"
            label="Interval Between Update Incident Emails (Minutes)"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableNumberConfiguration
            field="intervalBetweenUpdateIncidentAppPromptMinutes"
            label="Interval Between Update Incident App Prompt (Minutes)"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableEmbedDashboardConfiguration
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <OverrideableBooleanConfiguration
            field="embedPowerBiDashboard"
            label="V1 Dashboard Enabled"
            inheritedConfig={inheritedConfig}
            configuration={configuration}
            setConfiguration={setConfiguration}
          />
          <Box
            sx={{
              display: "flex",
              gap: "1rem",
              marginTop: "1rem",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Button
              variant="contained"
              color="inherit"
              onClick={() => {
                navigate(
                  createRoute.adminOrganisationManagement(organisation.id),
                );
              }}
            >
              Back to Org
            </Button>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={() => {
                setSubmitting(true);
                saveConfig(organisation, configuration)
                  .then(() => {
                    notifications.show("Organisation configuration saved", {
                      severity: "success",
                    });
                    setSubmitting(false);
                  })
                  .catch((e: unknown) => {
                    if (e instanceof Error) {
                      notifications.show(`Error saving config: ${e.message}`, {
                        severity: "error",
                      });
                    }
                    setSubmitting(false);
                  });
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    )
  );
}

function OrgConfigUpdate({ organisation }: { organisation: Organisation }) {
  const [{ data: organisationData, loading }] =
    apiHooks.organisation.getOrgConfigHierarchy(organisation.id);

  return loading || !organisationData ? (
    <CircularProgress />
  ) : (
    <OrgConfigUpdateInner
      organisation={organisation}
      organisationData={organisationData ?? []}
    />
  );
}

type OrganisationConfigManagementInnerProps = {
  organisations: Organisation[];
};

function OrganisationConfigManagementInner({
  organisations,
}: OrganisationConfigManagementInnerProps) {
  const { organisationId } = useParams();
  const navigate = useNavigate();
  const organisation = organisations.find((org) => org.id === organisationId);

  useEffect(() => {
    if (!organisation) {
      navigate(createRoute.home());
    }
  }, [organisation]);

  return organisation ? (
    <Box
      sx={{
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        maxWidth: "1200px",
        padding: "2rem",
      }}
    >
      <Typography variant="h4" component="div">
        Organisation Update
      </Typography>
      <OrgConfigUpdate organisation={organisation} />
    </Box>
  ) : (
    <CircularProgress />
  );
}

export function OrganisationConfigManagement() {
  const [{ data, loading }] = apiHooks.organisation.search("", 1000);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);

  useEffect(() => {
    if (data) {
      const orgs = OrganisationArraySchema.parse(data.results);
      setOrganisations(orgs);
    }
  }, [data]);

  return loading || organisations.length === 0 ? (
    <CircularProgress />
  ) : (
    <OrganisationConfigManagementInner organisations={organisations} />
  );
}
